import React, { useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';

import { FolderSelect } from 'components/forms/FolderSelect';
import type { CreateTarget } from 'actions';
import { useAppDispatch, useAppSelector } from 'redux/store';
import { createConnection, updateConnection, Connection, Worker } from '../../../../redux/slices/connectionsSlice';
import { getWorkersPaginated } from '../../../../redux/slices/workersSlice';
import { useUnsavedChanges } from '../../../../hooks/useUnsavedChanges';
import ConfirmationModal from '../../../../components/common/ConfirmationModal';

import ConnectionRest from './connection_rest';
import ConnectionDatabase from './connection_database';
import ConnectionSap from './connection_sap';
import ConnectionSAPIBP from './connection_sapibp';
import ConnectionSQLPlus from './connection_sqlplus';
import ConnectionMail from './connection_mail';
import ConnectionSSH from './connection_ssh';

export const normalizeConnectionType = (value: unknown): string =>
  String(value || '')
    .trim()
    .toLowerCase();

const emptyConnection = (): Connection => ({
  connection_id: 0,
  connection_name: '',
  connection_type: '',
  connection_parameters: {},
  workers: [],
  folder_id: null,
});

export interface EditConnectionModalProps {
  show: boolean;
  handleClose: () => void;
  mode: 'create' | 'edit';
  connection: Connection | null;
  // Workspace create pre-target: seeds the folder on create (connections are zoneless).
  createIn?: CreateTarget;
}

/**
 * Create / edit a connection. Self-contained: holds the form state, fetches the
 * workers list, renders the type-specific parameter sub-form, and persists via
 * the connection thunks (the slice reducers update the list in place).
 */
const EditConnectionModal: React.FC<EditConnectionModalProps> = ({ show, handleClose, mode, connection, createIn }) => {
  const dispatch = useAppDispatch();
  const workers = useAppSelector((state) => state.workers.items);

  const [connectionData, setConnectionData] = useState<Connection>(emptyConnection());
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isDirty = useUnsavedChanges(show, connectionData);

  useEffect(() => {
    if (show) dispatch(getWorkersPaginated({}) as any);
  }, [dispatch, show]);

  useEffect(() => {
    if (!show) return;
    if (mode === 'edit' && connection) {
      setConnectionData({ ...connection, connection_type: normalizeConnectionType(connection.connection_type) });
    } else {
      setConnectionData({ ...emptyConnection(), folder_id: createIn?.folderId ?? null });
    }
  }, [show, mode, connection, createIn]);

  const handleParameterChange = (key: string, value: any) => {
    setConnectionData((prev) => ({ ...prev, connection_parameters: { ...prev.connection_parameters, [key]: value } }));
  };

  const handleWorkerSelection = (worker: Worker, checked: boolean) => {
    setConnectionData((prev) => {
      const exists = prev.workers.some((w) => w.worker_id === worker.worker_id);
      let nextWorkers: Worker[];
      if (checked && !exists) {
        nextWorkers = [...prev.workers, { worker_id: worker.worker_id, worker_name: worker.worker_name, priority: 0 }];
      } else if (!checked && exists) {
        nextWorkers = prev.workers.filter((w) => w.worker_id !== worker.worker_id);
      } else {
        nextWorkers = prev.workers;
      }
      return { ...prev, workers: nextWorkers };
    });
  };

  const handleWorkerPriorityChange = (worker_id: number, priority: number) => {
    setConnectionData((prev) => ({
      ...prev,
      workers: prev.workers.map((w) => (w.worker_id === worker_id ? { ...w, priority } : w)),
    }));
  };

  const renderFormFields = () => {
    switch (normalizeConnectionType(connectionData.connection_type)) {
      case 'rest':
        return <ConnectionRest connectionData={connectionData} handleParameterChange={handleParameterChange} />;
      case 'database':
        return <ConnectionDatabase connectionData={connectionData} handleParameterChange={handleParameterChange} />;
      case 'sap':
        return <ConnectionSap connectionData={connectionData} handleParameterChange={handleParameterChange} />;
      case 'sqlplus':
        return <ConnectionSQLPlus connectionData={connectionData} handleParameterChange={handleParameterChange} />;
      case 'sapibp':
        return <ConnectionSAPIBP connectionData={connectionData} handleParameterChange={handleParameterChange} />;
      case 'mail':
        return <ConnectionMail connectionData={connectionData} handleParameterChange={handleParameterChange} />;
      case 'ssh':
        return <ConnectionSSH connectionData={connectionData} handleParameterChange={handleParameterChange} />;
      default:
        return null;
    }
  };

  const resetAndClose = () => {
    setConnectionData(emptyConnection());
    handleClose();
  };

  const handleRequestClose = () => {
    if (isDirty) {
      setShowCloseConfirm(true);
    } else {
      resetAndClose();
    }
  };

  const handleSave = async () => {
    const payload = { ...connectionData, connection_type: normalizeConnectionType(connectionData.connection_type) };
    setIsSubmitting(true);
    try {
      const result: any =
        mode === 'edit' && connection
          ? await dispatch(updateConnection({ ...connection, ...payload }) as any)
          : await dispatch(createConnection(payload) as any);
      if (result.meta.requestStatus === 'rejected') return;
      resetAndClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Modal show={show} onHide={handleRequestClose}>
        <Modal.Header closeButton>
          <Modal.Title>{mode === 'edit' ? 'Edit Connection' : 'New Connection'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="connectionName" className="mb-3">
              <Form.Label>Connection Name</Form.Label>
              <Form.Control
                type="text"
                value={connectionData.connection_name}
                onChange={(e) => setConnectionData((p) => ({ ...p, connection_name: e.target.value }))}
              />
            </Form.Group>

            <Form.Group controlId="connectionType" className="mb-3">
              <Form.Label>Connection Type</Form.Label>
              <Form.Select
                value={normalizeConnectionType(connectionData.connection_type)}
                onChange={(e) => setConnectionData((p) => ({ ...p, connection_type: normalizeConnectionType(e.target.value) }))}
              >
                <option value="">Select Connection Type</option>
                <option value="rest">REST</option>
                <option value="database">Database</option>
                <option value="sap">SAP</option>
                <option value="sapibp">SAPIBP</option>
                <option value="sqlplus">SQLPlus</option>
                <option value="mail">Mail</option>
                <option value="ssh">SSH</option>
              </Form.Select>
            </Form.Group>

            <Form.Group controlId="connectionFolder" className="mb-3">
              <Form.Label>Folder</Form.Label>
              <FolderSelect value={connectionData.folder_id} onChange={(folder_id) => setConnectionData((p) => ({ ...p, folder_id }))} />
            </Form.Group>

            {renderFormFields()}

            <Form.Group className="mb-3">
              <Form.Label>Workers</Form.Label>
              {workers.map((worker: Worker) => {
                const checked = connectionData.workers.some((w) => w.worker_id === worker.worker_id);
                return (
                  <div key={worker.worker_id} className="mb-2">
                    <Form.Check
                      type="checkbox"
                      label={worker.worker_name}
                      checked={checked}
                      onChange={(e) => handleWorkerSelection(worker, e.target.checked)}
                    />
                    {checked && (
                      <Form.Control
                        className="mt-2"
                        type="number"
                        value={connectionData.workers.find((w) => w.worker_id === worker.worker_id)?.priority ?? 0}
                        onChange={(e) => handleWorkerPriorityChange(worker.worker_id, parseInt(e.target.value || '0', 10))}
                        placeholder="Priority"
                      />
                    )}
                  </div>
                );
              })}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleRequestClose}>
            Close
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={isSubmitting}>
            Save and Close
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmationModal
        show={showCloseConfirm}
        title="Unsaved Changes"
        body="You have unsaved changes. Are you sure you want to close without saving?"
        confirmText="Discard Changes"
        confirmVariant="danger"
        onConfirm={() => {
          setShowCloseConfirm(false);
          resetAndClose();
        }}
        onCancel={() => setShowCloseConfirm(false)}
      />
    </>
  );
};

export default EditConnectionModal;
